import type { MergeAuditPreviewEntry } from '~/api/civilsearch';
import type { Agency } from '~/common/types';

export type MergeAuditPanelProps = {
  /** The merge inputs in selection order; the table has one row per input. */
  agencies: Array<Pick<Agency, 'id' | 'agencyName'>>;
  previews: MergeAuditPreviewEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Called each time the table is expanded. */
  onExpand?: () => void;
};

type AuditCounts = {
  dataRequests: number;
  linksMove: number;
  alreadyLinked: number;
  contactsMove: number;
  otherMove: number;
  stayOnChild: number;
};

const COLUMNS: Array<{ key: keyof AuditCounts; label: string }> = [
  { key: 'dataRequests', label: 'Data requests' },
  { key: 'linksMove', label: 'Project links move' },
  { key: 'alreadyLinked', label: 'Already linked' },
  { key: 'contactsMove', label: 'Contacts move' },
  { key: 'otherMove', label: 'Other records move' },
  { key: 'stayOnChild', label: 'Stay on child' },
];

/** Per-input counts; "stay on child" covers duplicates kept on the input and DRF requests not yet migrated. */
export function auditCountsFor(preview: MergeAuditPreviewEntry): AuditCounts {
  const r = preview.repointed;
  return {
    dataRequests: r.dataRequestsV2,
    linksMove: r.projectAgencies.willMove,
    alreadyLinked: r.projectAgencies.alreadyLinked,
    contactsMove: r.agencyContacts.willMove,
    otherMove:
      r.projectUtilityNotes.willMove +
      r.agencyLinks.willMove +
      r.agencyNotes +
      r.agencyDigitization.willMove +
      r.intelligenceSummaryUndigitizedAgencies.willMove,
    stayOnChild:
      r.projectAgencies.willStay +
      r.projectUtilityNotes.willStayOnChild +
      r.agencyContacts.willStayOnChild +
      r.agencyLinks.willStayOnChild +
      r.agencyDigitization.willStayOnChild +
      r.intelligenceSummaryUndigitizedAgencies.willStayOnChild +
      preview.stayingBehind.drfRequests,
  };
}

export function MergeAuditPanel({ agencies, previews, isLoading, isError, onExpand }: MergeAuditPanelProps) {
  const failed = !isLoading && (isError || !previews);
  const loaded = isLoading || failed ? undefined : previews;
  const byId = new Map((loaded ?? []).map((p) => [p.agencyId, p]));
  const rows = agencies.flatMap((agency, index) => {
    const preview = byId.get(agency.id);
    return preview ? [{ agency, index, counts: auditCountsFor(preview) }] : [];
  });
  const totalLinksMove = rows.reduce((sum, row) => sum + row.counts.linksMove, 0);
  const overlaps = (loaded ?? []).flatMap((p) => p.blockers?.overlappingSubGeometries ?? []);

  // Both live regions mount empty while the preview loads, so the error and the blocker are announced when they appear.
  return (
    <div className="flex flex-col">
      <output aria-live="polite" className="block" data-testid="merge-audit-status">
        {failed && (
          <div
            data-testid="merge-audit-error"
            className="rounded-md border border-grey-30 bg-grey-10 px-4 py-3 text-sm text-grey-70"
          >
            Couldn't load merge audit — proceed with caution.
          </div>
        )}
      </output>
      <div role="alert" data-testid="merge-audit-alert">
        {overlaps.length > 0 && (
          <div
            data-testid="merge-audit-overlap-blocker"
            className="mb-3 rounded-md border border-red-400 bg-red-100 px-4 py-3 text-sm text-red-900"
          >
            <div className="mb-1 font-semibold">Cannot merge — sub-regions would overlap on the merged agency:</div>
            <ul className="list-disc pl-5">
              {overlaps.map((o, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: overlap pairs have no stable id
                <li key={i} data-testid="merge-audit-overlap-row">
                  "{o.source.name ?? o.source.id}" overlaps "{o.target.name ?? o.target.id}"
                </li>
              ))}
            </ul>
            <div className="mt-1">Resolve the overlap (edit or remove a sub-region) before merging.</div>
          </div>
        )}
      </div>
      {isLoading && (
        <div data-testid="merge-audit-loading" className="rounded-md border border-grey-30 bg-grey-10 px-4 py-3">
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-4 w-full rounded bg-grey-20 motion-safe:animate-pulse" />
            ))}
          </div>
        </div>
      )}
      {loaded && (
        <div data-testid="merge-audit-panel" className="flex flex-col gap-3">
          <details
            className="rounded-md border border-grey-30"
            onToggle={(e) => {
              if (e.currentTarget.open) onExpand?.();
            }}
          >
            <summary
              data-testid="merge-audit-summary"
              className="focus-ring flex cursor-pointer flex-wrap items-center gap-3 rounded-md px-4 py-2.5 text-sm font-medium text-primary-dark"
            >
              <span>What moves to the new parent</span>
              <span data-testid="merge-audit-total" className="ml-auto font-normal text-grey-70 tabular-nums">
                {totalLinksMove} project {totalLinksMove === 1 ? 'link moves' : 'links move'}
              </span>
            </summary>
            <table className="w-full table-fixed border-collapse text-xs text-primary-dark">
              <caption className="sr-only">Records that move from each selected agency to the new parent</caption>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="w-2/7 border-t border-grey-30 px-4 py-1.5 text-left font-medium text-grey-70"
                  >
                    Agency
                  </th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className="border-t border-grey-30 px-2 py-1.5 text-right font-medium text-grey-70"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ agency, index, counts }) => (
                  <tr key={agency.id} data-testid={`merge-audit-row-${agency.id}`}>
                    <th scope="row" className="border-t border-grey-30 px-4 py-1.5 text-left font-normal break-words">
                      {index + 1}. {agency.agencyName}
                    </th>
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        data-testid={`merge-audit-${col.key}-${agency.id}`}
                        className="border-t border-grey-30 px-2 py-1.5 text-right tabular-nums"
                      >
                        {counts[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}
    </div>
  );
}
